import React from 'react';
import { Link } from 'react-router-dom';
import { PageTitle } from '../components/PageTitle';
import { ArrowLeftIcon, BarChart2Icon, TrendingUpIcon, UsersIcon, ClockIcon, TargetIcon, LineChartIcon } from 'lucide-react';
export function CaseStudy2() {
  return <div data-id="element-480">
      <PageTitle title="Go-to-Market Strategy" description="How I spearheaded product growth strategy and GTM execution for a $55M SaaS/IoT product portfolio, driving 93% trial-to-purchase conversion." data-id="element-481" />
      <section className="bg-gradient-to-r from-[#0096B4] to-[#7DCCD8] text-white py-16" data-id="element-482">
        <div className="container mx-auto px-4" data-id="element-483">
          <Link to="/" className="inline-flex items-center text-white hover:text-[#E0F5F8] mb-6 transition-colors duration-200" data-id="element-484">
            <ArrowLeftIcon size={16} className="mr-1" data-id="element-485" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold mb-4" data-id="element-486">Go-to-Market Strategy</h1>
          <p className="text-xl mb-6 text-white max-w-3xl" data-id="element-487">
            How I spearheaded end-to-end product growth strategy, feature
            roadmap, and GTM execution for a $55M SaaS/IoT product portfolio,
            driving 93% trial-to-purchase conversion and record quarterly
            pipeline.
          </p>
          <div className="flex flex-wrap gap-6 mt-8" data-id="element-488">
            <div className="flex items-center" data-id="element-489">
              <BarChart2Icon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-490" />
              <span className="text-white" data-id="element-491">93% Conversion Rate</span>
            </div>
            <div className="flex items-center" data-id="element-492">
              <TrendingUpIcon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-493" />
              <span className="text-white" data-id="element-494">$34M Quarterly Pipeline</span>
            </div>
            <div className="flex items-center" data-id="element-495">
              <UsersIcon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-496" />
              <span className="text-white" data-id="element-497">8-Member Task Force</span>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 bg-white" data-id="element-498">
        <div className="container mx-auto px-4" data-id="element-499">
          <div className="max-w-3xl mx-auto" data-id="element-500">
            <div className="mb-12" data-id="element-501">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-502">
                The Challenge
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-503">
                Despite having innovative technology and a $55M SaaS/IoT product
                portfolio with strong potential, the company was facing stalled
                growth and limited hardware sales.
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-504">
                <li data-id="element-505">Sales cycle was lengthy with inconsistent close rates</li>
                <li data-id="element-506">
                  Internal assessment revealed several bottlenecks in the sales
                  organization
                </li>
                <li data-id="element-507">
                  The company needed a comprehensive strategy to accelerate
                  market penetration
                </li>
              </ul>
            </div>
            <div className="mb-12" data-id="element-508">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-509">
                My Approach
              </h2>
              <ol className="list-decimal pl-6 space-y-6" data-id="element-510">
                <li data-id="element-511">
                  <div data-id="element-512">
                    <span className="font-medium text-gray-900" data-id="element-513">
                      Comprehensive Market Research
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-514">
                      <li data-id="element-515">
                        Conducted 100+ in-depth interviews with customers and
                        partners to identify friction points and opportunities
                      </li>
                      <li data-id="element-516">
                        Refined user personas and mapped the customer journey to
                        clarify our unique advantage
                      </li>
                    </ul>
                  </div>
                </li>
                <li data-id="element-517">
                  <div data-id="element-518">
                    <span className="font-medium text-gray-900" data-id="element-519">
                      Cross-Functional Task Force Leadership
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-520">
                      <li data-id="element-521">
                        Assembled and led an 8-member team from product, sales,
                        engineering, data science, and marketing
                      </li>
                      <li data-id="element-522">
                        Implemented agile methodologies to quickly iterate on
                        solutions with clear accountability metrics
                      </li>
                    </ul>
                  </div>
                </li>
                <li data-id="element-523">
                  <div data-id="element-524">
                    <span className="font-medium text-gray-900" data-id="element-525">
                      Targeted Sales Enablement & Experimentation
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-526">
                      <li data-id="element-527">
                        Developed new positioning and comprehensive sales
                        toolkit based on customer insights
                      </li>
                      <li data-id="element-528">
                        Launched a 2-week positioning experiment with 33 SDRs to
                        validate approach before full-scale rollout
                      </li>
                    </ul>
                  </div>
                </li>
              </ol>
            </div>
            <div className="mb-12" data-id="element-529">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-530">
                Execution & Results
              </h2>
              <div className="bg-[#F0FAFC] p-6 rounded-lg mb-8" data-id="element-531">
                <h3 className="text-xl font-semibold text-gray-800 mb-3" data-id="element-532">
                  Key Initiatives Implemented:
                </h3>
                <ul className="space-y-3" data-id="element-533">
                  <li className="flex" data-id="element-534">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-535"></div>
                    <div data-id="element-536">
                      <span className="font-medium text-gray-900" data-id="element-537">
                        Targeted Sales Motion Relaunch
                      </span>
                      <p className="text-gray-700" data-id="element-538">
                        Relaunched product with refined positioning and sales
                        approach based on extensive research
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-539">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-540"></div>
                    <div data-id="element-541">
                      <span className="font-medium text-gray-900" data-id="element-542">
                        Enhanced Demo Environment
                      </span>
                      <p className="text-gray-700" data-id="element-543">
                        Partnered with engineering to develop and launch updated
                        tool in just 2 weeks to showcase desired features during
                        demos
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-544">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-545"></div>
                    <div data-id="element-546">
                      <span className="font-medium text-gray-900" data-id="element-547">
                        Comprehensive Sales Training Program
                      </span>
                      <p className="text-gray-700" data-id="element-548">
                        Trained 500+ sales representatives on new positioning,
                        objection handling, and demo techniques
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3" data-id="element-549">
                Impact:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-550">
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-551">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-552">
                    93%
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-553">
                    Trial-to-Purchase Conversion
                  </div>
                </div>
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-554">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-555">
                    $34M
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-556">
                    Quarterly Pipeline
                  </div>
                </div>
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-557">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-558">
                    -20%
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-559">
                    Sales Cycle Reduction
                  </div>
                </div>
              </div>
              <p className="text-gray-700" data-id="element-560">
                The new GTM strategy delivered exceptional results across all
                key metrics. Trial-to-purchase conversion increased from the
                industry benchmark of 30-40% to an impressive 93%. We generated
                a record $34M quarterly pipeline and drove $6.5M in revenue
                within just 6 weeks of implementation, while reducing the sales
                cycle by 20% and improving close rates by 12%.
              </p>
            </div>
            <div data-id="element-561">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-562">
                Key Learnings
              </h2>
              <ol className="list-decimal pl-6 space-y-4" data-id="element-563">
                <li data-id="element-564">
                  <div data-id="element-565">
                    <span className="font-medium text-gray-900" data-id="element-566">
                      Deep customer understanding drives effective positioning
                    </span>
                    <p className="text-gray-700" data-id="element-567">
                      The extensive customer and partner interviews provided
                      invaluable insights that shaped our messaging and
                      approach, creating a foundation for all other
                      improvements.
                    </p>
                  </div>
                </li>
                <li data-id="element-568">
                  <div data-id="element-569">
                    <span className="font-medium text-gray-900" data-id="element-570">
                      Cross-functional collaboration accelerates problem-solving
                    </span>
                    <p className="text-gray-700" data-id="element-571">
                      The 8-member task force allowed us to quickly identify and
                      address barriers across the entire customer journey,
                      breaking down organizational silos.
                    </p>
                  </div>
                </li>
                <li data-id="element-572">
                  <div data-id="element-573">
                    <span className="font-medium text-gray-900" data-id="element-574">
                      Rapid experimentation validates strategy
                    </span>
                    <p className="text-gray-700" data-id="element-575">
                      The 2-week positioning test provided quick validation and
                      allowed us to refine our approach before full-scale
                      implementation, demonstrating the power of lean
                      methodologies.
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>
      <section className="py-12 bg-[#F0FAFC]" data-id="element-576">
        <div className="container mx-auto px-4" data-id="element-577">
          <div className="max-w-3xl mx-auto" data-id="element-578">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-579">
              More Case Studies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-580">
              <Link to="/case-study-1" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" data-id="element-581">
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-582">
                  Product Turnaround Strategy
                </h3>
                <p className="text-gray-600 mb-3" data-id="element-583">
                  Drove turnaround of $110M product platform from -12% decline
                  to +9% growth.
                </p>
                <span className="text-[#0096B4] font-medium" data-id="element-584">
                  Read case study →
                </span>
              </Link>
              <Link to="/case-study-3" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" data-id="element-585">
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-586">
                  CRM Program Development
                </h3>
                <p className="text-gray-600 mb-3" data-id="element-587">
                  Built Neutrogena's CRM program, fueling +236% traffic growth.
                </p>
                <span className="text-[#0096B4] font-medium" data-id="element-588">
                  Read case study →
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
}